import { useState, useEffect, useRef,memo } from "react"
import io from 'socket.io-client';
import Peer from 'simple-peer';
import { useMemo } from "react";
// 스타일 임포트
import {
  ModalContainer,
  FlexColumn,
  Header,
  HeaderLeft,
  HeaderRight,
  Title,
  ParticipantCount,
  ParticipantCountText,
  MainContent,
  VideoGrid,
  Controls,
  Button,
  VideoContainer,
  VideoPlaceholder,
  VideoFrame,
  VideoImage,
  AudioLevel,
  VideoInfo,
  VideoInfoContent,
  VideoInfoLeft,
  VideoInfoRight,
  VideoName,
  Avatar,
  AvatarImage,
  Icon,
  ToolboxContainer,
  ToolboxButton,
  ParticipantDrawer,
  ParticipantItem,
  ParticipantInfo,
  ParticipantActions,
  DrawerHeader,
  MediaSelectorOverlay,
  MediaSelectorCard,
  MediaSelectorTitle,
  MediaOptionContainer,
  MediaOptionButton,
  IconContainer,
  OptionLabel,
  OptionDescription,
  colors
} from "./FaceTalkStyle";

// 아이콘 임포트
import {
  XIcon,
  MicIcon,
  MicOffIcon,
  VideoIcon,
  VideoOffIcon,
  Share2Icon,
  PhoneOffIcon,
  UsersIcon,
  Volume2Icon,
  SettingsIcon,
  EmojiIcon,
  HandIcon,
  ChatIcon,
  UserPlusIcon
} from "./FaceTalkStyle";
// 추가 import


// 시간 표시를 위한 분리된 메모이제이션 컴포넌트
const MeetingTimer = memo(({ seconds }) => {
  const formattedTime = useMemo(() => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, [seconds]);
  
  return <span>{formattedTime}</span>;
});

// 연결 상태 컴포넌트
const ConnectionStatus = memo(({ status }) => {
  switch(status) {
    case 'connected':
      return <span style={{color: 'green'}}>연결됨</span>;
    case 'disconnected':
      return <span style={{color: 'red'}}>연결 끊김</span>;
    case 'error':
      return <span style={{color: 'orange'}}>연결 오류</span>;
    default:
      return null;
  }
});

const VideoConferenceModal = ({ isOpen, onClose, roomId }) => {
  // 상태 관리
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRaiseHand, setIsRaiseHand] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [meetingTime, setMeetingTime] = useState(0);
  const [actualParticipantCount, setActualParticipantCount] = useState(0);
  const [displayedParticipantCount, setDisplayedParticipantCount] = useState(5); // 기본값 5명으로 설정
  const [isParticipantDrawerOpen, setIsParticipantDrawerOpen] = useState(false);
  const [isToolboxOpen, setIsToolboxOpen] = useState(false);
  const [peers, setPeers] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [mediaType, setMediaType] = useState(null); // 'camera' 또는 'screen'
  const [showMediaSelector, setShowMediaSelector] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const meetingTimeRef = useRef(0);
  // 참조 객체
  const socketRef = useRef();
  const userVideoRef = useRef();
  const peersRef = useRef([]);
  const localStreamRef = useRef();

  // 미디어 타입 선택 함수
  const selectMediaType = (type) => {
    setMediaType(type);
    setShowMediaSelector(false);
    
    if (type === 'camera') {
      // 카메라 스트림 가져오기
      navigator.mediaDevices.getUserMedia({ video: !isVideoOff, audio: !isMuted })
        .then(handleStreamSuccess)
        .catch(handleStreamError);
    } else if (type === 'screen') {
      // 화면 공유 스트림 가져오기
      navigator.mediaDevices.getDisplayMedia({ 
        video: { cursor: "always" },
        audio: true 
      })
        .then(handleStreamSuccess)
        .catch(error => {
          console.error("화면 공유 오류:", error);
          alert(`화면 공유를 시작할 수 없습니다: ${error.message}`);
          // 오류 시 카메라로 폴백
          setMediaType('camera');
          navigator.mediaDevices.getUserMedia({ video: !isVideoOff, audio: !isMuted })
            .then(handleStreamSuccess)
            .catch(handleStreamError);
        });
    }
  };

  // 스트림 성공 처리 함수
  const handleStreamSuccess = (stream) => {
    console.log(`${mediaType === 'camera' ? '카메라' : '화면 공유'} 스트림 획득 성공`);
    
    userVideoRef.current.srcObject = stream;
    localStreamRef.current = stream;
    
    // 화면 공유인 경우 종료 이벤트 리스너 추가
    if (mediaType === 'screen') {
      setIsScreenSharing(true);
      const screenTrack = stream.getVideoTracks()[0];
      
      screenTrack.onended = () => {
        console.log('사용자가 화면 공유를 중지함');
        setIsScreenSharing(false);
        // 화면 공유 종료 시 카메라로 전환
        setMediaType('camera');
        navigator.mediaDevices.getUserMedia({ video: !isVideoOff, audio: !isMuted })
          .then(handleStreamSuccess)
          .catch(handleStreamError);
      };
    }
    
    // 방 참여
    const roomToJoin = roomId || `default-room-${Date.now()}`;
    console.log(`방 참여 시도: ${roomToJoin}`);
    socketRef.current.emit('join room', roomToJoin);
    
    // 소켓 이벤트 리스너 설정
    setupSocketListeners(stream);
  };

  // 스트림 오류 처리 함수
  const handleStreamError = (err) => {
    console.error("미디어 장치 접근 오류:", err);
    
    // 오류 유형에 따라 다른 처리
    if (err.name === 'NotFoundError') {
      alert('카메라 또는 마이크를 찾을 수 없습니다. 장치가 연결되어 있는지 확인해주세요.');
      
      // 비디오 없이 오디오만으로 시도
      return navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then(handleStreamSuccess)
        .catch(error => {
          console.error("오디오만 사용 시도 중 오류:", error);
          // 빈 미디어 스트림으로 참여
          handleEmptyStream();
        });
    } else if (err.name === 'NotAllowedError') {
      alert('카메라와 마이크 접근 권한이 필요합니다. 브라우저 권한을 확인해주세요.');
    } else {
      alert(`미디어 장치 접근 오류: ${err.message}`);
    }
    
    // 빈 미디어 스트림으로 참여
    handleEmptyStream();
  };

  // 빈 스트림으로 참여하는 함수
  const handleEmptyStream = () => {
    console.log('미디어 장치 없이 참여합니다');
    setIsVideoOff(true);
    setIsMuted(true);
    
    // 빈 스트림 생성
    const emptyStream = new MediaStream();
    userVideoRef.current.srcObject = emptyStream;
    localStreamRef.current = emptyStream;
    
    // 방 참여
    const roomToJoin = roomId || `default-room-${Date.now()}`;
    socketRef.current.emit('join room', roomToJoin);
    
    // 소켓 이벤트 리스너 설정
    setupSocketListeners(emptyStream);
  };

  // 소켓 이벤트 리스너 설정 함수
  const setupSocketListeners = (stream) => {
    // 참가자 수 업데이트
    socketRef.current.on('user count', count => {
      console.log(`참가자 수 업데이트: ${count}`);
      setActualParticipantCount(count);
      // 실제 참가자 수가 표시 수보다 작으면 표시 수도 업데이트
      if (count < displayedParticipantCount) {
        setDisplayedParticipantCount(count);
      }
    });

    // 기존 사용자들에게 연결
    socketRef.current.on('all users', users => {
      console.log('기존 사용자 목록 수신:', users);
      const peers = [];
      users.forEach(userID => {
        console.log(`기존 사용자에게 연결 시도: ${userID}`);
        const peer = createPeer(userID, socketRef.current.id, stream);
        peersRef.current.push({
          peerID: userID,
          peer,
        });
        peers.push({
          peerID: userID,
          peer,
        });
      });
      setPeers(peers);
    });

    // 새 사용자 연결
    socketRef.current.on('user joined', payload => {
      console.log(`새 사용자가 참여함: ${payload.callerID}`);
      const peer = addPeer(payload.signal, payload.callerID, stream);
      peersRef.current.push({
        peerID: payload.callerID,
        peer,
      });

      setPeers(peers => [...peers, {
        peerID: payload.callerID,
        peer,
      }]);
    });

    // 응답 신호 받기
    socketRef.current.on('receiving returned signal', payload => {
      console.log(`응답 신호 수신: ${payload.id}`);
      const item = peersRef.current.find(p => p.peerID === payload.id);
      if (item) {
        item.peer.signal(payload.signal);
      } else {
        console.warn(`해당 피어를 찾을 수 없음: ${payload.id}`);
      }
    });
    
    // 사용자 퇴장 이벤트
    socketRef.current.on('user left', userId => {
      console.log(`사용자 퇴장: ${userId}`);
      peersRef.current = peersRef.current.filter(p => p.peerID !== userId);
      setPeers(peers => peers.filter(p => p.peerID !== userId));
    });

    // 손들기 이벤트 수신
    socketRef.current.on('user raised hand', payload => {
      console.log(`사용자 ${payload.userId}가 손을 ${payload.raised ? '들었습니다' : '내렸습니다'}`);
      // 여기에 UI 업데이트 로직 추가 가능
    });
  };

  // 모바일 감지
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 연결 설정 및 정리
  useEffect(() => {
    if (!isOpen) return;

    console.log('화상 회의 컴포넌트 마운트. 룸 ID:', roomId || 'default-room');

    // 소켓 연결 - 서버에 맞게 URL 수정
    socketRef.current = io.connect(window.location.origin, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    
    // 연결 상태 이벤트 리스너
    socketRef.current.on('connect', () => {
      console.log('소켓 서버에 연결됨. 소켓 ID:', socketRef.current.id);
      setConnectionStatus('connected');
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('소켓 연결 오류:', error);
      setConnectionStatus('error');
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('소켓 연결 끊김:', reason);
      setConnectionStatus('disconnected');
    });
    
    // 미디어 접근은 사용자 선택 후에 수행됨
    
    // 타이머 설정
    const timer = setInterval(() => {
      meetingTimeRef.current += 1;
      // 1초마다 상태 업데이트하는 대신 3초마다 업데이트하여 리렌더링 횟수 줄임
      if (meetingTimeRef.current % 3 === 0) {
        setMeetingTime(meetingTimeRef.current);
      }
    }, 1000);

    // 컴포넌트 언마운트 시 정리
    return () => {
      console.log('화상 회의 컴포넌트 언마운트. 연결 정리 중...');
      
      // 소켓 연결 해제
      if (socketRef.current) {
        const roomToLeave = roomId || `default-room-${Date.now()}`;
        socketRef.current.emit('leave room', roomToLeave);
        socketRef.current.disconnect();
        console.log('소켓 연결 해제됨');
      }
      
      // 비디오 트랙 중지
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log(`트랙 중지됨: ${track.kind}`);
        });
      }
      
      // 피어 연결 해제
      peersRef.current.forEach(peer => {
        if (peer.peer) {
          peer.peer.destroy();
          console.log(`피어 연결 해제: ${peer.peerID}`);
        }
      });
      
      // 타이머 정리
      clearInterval(timer);
    };
  }, [isOpen, roomId]);

  // ICE 서버 설정 (STUN/TURN)
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
      { urls: 'stun:stun4.l.google.com:19302' },
    ]
  };

  // 피어 생성 함수
  function createPeer(userToSignal, callerID, stream) {
    console.log(`피어 생성: ${userToSignal}`);
    const peer = new Peer({
      initiator: true,
      trickle: false,
      stream,
      config: iceServers,
    });

    peer.on('signal', signal => {
      console.log(`신호 생성됨 (initiator): ${userToSignal}`);
      socketRef.current.emit('sending signal', { userToSignal, callerID, signal });
    });

    peer.on('stream', remoteStream => {
      console.log(`원격 스트림 수신 (initiator): ${userToSignal}`);
      setPeers(prev => prev.map(p =>
        p.peerID === userToSignal ? { ...p, remoteStream } : p
      ));
    });

    peer.on('error', err => {
      console.error(`피어 오류 (initiator): ${err.message}`);
    });

    return peer;
  }

  // 피어 추가 함수
  function addPeer(incomingSignal, callerID, stream) {
    console.log(`피어 추가: ${callerID}`);
    const peer = new Peer({
      initiator: false,
      trickle: false,
      stream,
      config: iceServers,
    });

    peer.on('signal', signal => {
      console.log(`신호 생성됨 (receiver): ${callerID}`);
      socketRef.current.emit('returning signal', { signal, callerID });
    });

    peer.on('stream', remoteStream => {
      console.log(`원격 스트림 수신 (receiver): ${callerID}`);
      setPeers(prev => prev.map(p =>
        p.peerID === callerID ? { ...p, remoteStream } : p
      ));
    });

    peer.on('error', err => {
      console.error(`피어 오류 (receiver): ${err.message}`);
    });

    peer.signal(incomingSignal);

    return peer;
  }

  // 오디오 토글
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = isMuted;
        console.log(`오디오 트랙 상태 변경: ${track.enabled ? '켜짐' : '꺼짐'}`);
      });
      setIsMuted(!isMuted);
    }
  };

  // 비디오 토글
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = isVideoOff;
        console.log(`비디오 트랙 상태 변경: ${track.enabled ? '켜짐' : '꺼짐'}`);
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  // 화면 공유 토글
  const toggleScreenSharing = () => {
    if (!isScreenSharing) {
      console.log('화면 공유 시작');
      navigator.mediaDevices.getDisplayMedia({ 
        video: { cursor: "always" },
        audio: true 
      })
        .then(stream => {
          const screenTrack = stream.getVideoTracks()[0];
          console.log('화면 공유 스트림 획득 성공');
          
          // 기존 피어 연결에 화면 공유 트랙 교체
          peersRef.current.forEach(({ peer }) => {
            try {
              console.log('피어에 화면 공유 트랙 교체 시도');
              // 피어가 RTCPeerConnection에 직접 접근할 수 있는지 확인
              if (peer._pc && peer._pc.getSenders) {
                const sender = peer._pc.getSenders().find(s => s.track && s.track.kind === 'video');
                if (sender) {
                  sender.replaceTrack(screenTrack);
                  console.log('트랙 교체 성공');
                } else {
                  console.warn('비디오 송신자를 찾을 수 없음');
                }
              } else {
                console.warn('피어가 getSenders 메소드를 지원하지 않음');
              }
            } catch (error) {
              console.error("트랙 교체 오류:", error);
            }
          });

          // 화면 공유 종료 이벤트 리스너
          screenTrack.onended = () => {
            console.log('사용자가 화면 공유를 중지함');
            localStreamRef.current.getVideoTracks().forEach(track => {
              // 기존 피어 연결에 원래 비디오 트랙으로 교체
              peersRef.current.forEach(({ peer }) => {
                try {
                  if (peer._pc && peer._pc.getSenders) {
                    const sender = peer._pc.getSenders().find(s => s.track && s.track.kind === 'video');
                    if (sender) {
                      sender.replaceTrack(track);
                      console.log('원래 비디오 트랙으로 복원 성공');
                    }
                  }
                } catch (error) {
                  console.error("트랙 복원 오류:", error);
                }
              });
            });
            setIsScreenSharing(false);
          };

          // 로컬 비디오에 화면 공유 표시
          const newStream = new MediaStream([screenTrack, ...localStreamRef.current.getAudioTracks()]);
          userVideoRef.current.srcObject = newStream;
          setIsScreenSharing(true);
        })
        .catch(err => {
          console.error("화면 공유 오류:", err);
          alert(`화면 공유를 시작할 수 없습니다: ${err.message}`);
        });
    } else {
      console.log('화면 공유 중지');
      // 화면 공유 중지 및 원래 비디오로 복원
      localStreamRef.current.getVideoTracks().forEach(track => {
        peersRef.current.forEach(({ peer }) => {
          try {
            if (peer._pc && peer._pc.getSenders) {
              const sender = peer._pc.getSenders().find(s => s.track && s.track.kind === 'video');
              if (sender) {
                sender.replaceTrack(track);
                console.log('원래 비디오 트랙으로 복원 성공');
              }
            }
          } catch (error) {
            console.error("트랙 복원 오류:", error);
          }
        });
      });
      
      userVideoRef.current.srcObject = localStreamRef.current;
      setIsScreenSharing(false);
    }
  };

  // 참가자 수 증가/감소 함수 수정
  const decreaseParticipants = () => {
    if (displayedParticipantCount > 2) {
      setDisplayedParticipantCount(displayedParticipantCount - 1);
      console.log(`표시 참가자 수 감소: ${displayedParticipantCount - 1}`);
    }
  };

  const increaseParticipants = () => {
    console.log("현재:", displayedParticipantCount);
    if (displayedParticipantCount < 8) {
      const newCount = displayedParticipantCount + 1;
      console.log("변경 후:", newCount);
      setDisplayedParticipantCount(newCount);
    }
  };

  // 참가자 드로어 토글
  const toggleParticipantDrawer = () => {
    setIsParticipantDrawerOpen(!isParticipantDrawerOpen);
  };

  // 툴박스 토글
  const toggleToolbox = () => {
    setIsToolboxOpen(!isToolboxOpen);
  };
  
  // 손들기 토글
  const toggleRaiseHand = () => {
    setIsRaiseHand(!isRaiseHand);
    // 손들기 이벤트를 서버에 전송할 수 있음
    if (socketRef.current) {
      console.log(`손들기 상태 변경: ${!isRaiseHand}`);
      socketRef.current.emit('raise hand', { raised: !isRaiseHand, userId: socketRef.current.id });
    }
  };

  // 미디어 선택 모달
  const renderMediaSelector = () => {
    if (!showMediaSelector) return null;
    
    return (
      <MediaSelectorOverlay>
        <MediaSelectorCard>
          <MediaSelectorTitle>참여 방법을 선택하세요</MediaSelectorTitle>
          <MediaOptionContainer>
            <MediaOptionButton 
              onClick={() => selectMediaType('camera')}
              color={colors.primary}
            >
              <IconContainer color="79, 70, 229">
                <VideoIcon style={{ width: "2.5rem", height: "2.5rem", color: colors.primary }} />
              </IconContainer>
              <OptionLabel>화상 카메라</OptionLabel>
              <OptionDescription>카메라와 마이크 사용</OptionDescription>
            </MediaOptionButton>
            
            <MediaOptionButton 
              onClick={() => selectMediaType('screen')}
              color={colors.secondary}
            >
              <IconContainer color="14, 165, 233">
                <Share2Icon style={{ width: "2.5rem", height: "2.5rem", color: colors.secondary }} />
              </IconContainer>
              <OptionLabel>화면 공유</OptionLabel>
              <OptionDescription>화면과 오디오 공유</OptionDescription>
            </MediaOptionButton>
          </MediaOptionContainer>
        </MediaSelectorCard>
      </MediaSelectorOverlay>
    );
  };
  // 참가자 비디오 렌더링
  const renderParticipantVideo = (participant, isSmall = false) => {
    return (
      <VideoContainer>
        {!participant.stream || participant.isVideoOff ? (
          <VideoPlaceholder>
            {/* <Avatar small={isSmall}>
              {participant.name ? (
                <AvatarImage src={`https://ui-avatars.com/api/?name=${participant.name}&background=random`} alt={participant.name} />
              ) : (
                <div>{participant.peerID?.substring(0, 1) || "U"}</div>
              )}
            </Avatar> */}
          </VideoPlaceholder>
        ) : (
          <VideoFrame>
            <video
              ref={video => {
                if (video && participant.stream) {
                  video.srcObject = participant.stream;
                }
              }}
              autoPlay
              playsInline
              muted={participant.isMuted}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <AudioLevel style={{ transform: `scaleX(${participant.audioLevel || 0})` }} />
          </VideoFrame>
        )}

        <VideoInfo>
          <VideoInfoContent>
            <VideoInfoLeft>
              {participant.isSpeaking && (
                <Icon>
                  <Volume2Icon />
                </Icon>
              )}
              <VideoName small={isSmall} inVideo={true}>
                {participant.name || `User ${participant.peerID?.substring(0, 4)}`}
              </VideoName>
            </VideoInfoLeft>
            <VideoInfoRight>
              {participant.isMuted && (
                <Icon>
                  <MicOffIcon />
                </Icon>
              )}
              {participant.isVideoOff && (
                <Icon>
                  <VideoOffIcon />
                </Icon>
              )}
            </VideoInfoRight>
          </VideoInfoContent>
        </VideoInfo>
      </VideoContainer>
    );
  };

  // 참가자 드로어 렌더링
  const renderParticipantDrawer = () => {
    const participantsList = [
      // 로컬 사용자
      {
        id: 'local',
        name: 'You (Host)',
        avatar: `https://ui-avatars.com/api/?name=You&background=random`,
        isSpeaking: false,
        isHost: true,
        isMuted: isMuted,
        isVideoOff: isVideoOff,
        isScreenSharing: isScreenSharing,
        audioLevel: 0,
      },
      // 원격 참가자들 (피어)
      ...peers.map(peer => ({
        id: peer.peerID,
        name: `User ${peer.peerID.substring(0, 4)}`,
        avatar: `https://ui-avatars.com/api/?name=${peer.peerID.substring(0, 2)}&background=random`,
        isSpeaking: false,
        isHost: false,
        isMuted: false, // 실제 상태는 피어 연결에서 가져와야 함
        isVideoOff: false, // 실제 상태는 피어 연결에서 가져와야 함
        isScreenSharing: false,
        audioLevel: 0,
      }))
    ];

    return (
      <ParticipantDrawer isOpen={isParticipantDrawerOpen}>
        <DrawerHeader>
          <h3>참여자 ({participantsList.length})</h3>
          <Button variant="ghost" onClick={toggleParticipantDrawer}>
            <XIcon />
          </Button>
        </DrawerHeader>
        {participantsList.map((participant) => (
          <ParticipantItem key={participant.id}>
            <Avatar small>
              <AvatarImage src={participant.avatar} alt={participant.name} />
            </Avatar>
            <ParticipantInfo>
              <VideoName inVideo={false}>{participant.name}</VideoName>
              {participant.isHost && <span style={{ fontSize: "0.75rem" }}>호스트</span>}
            </ParticipantInfo>
            <ParticipantActions>
              {participant.isSpeaking && (
                <Icon>
                  <Volume2Icon />
                </Icon>
              )}
              {participant.isMuted && (
                <Icon>
                  <MicOffIcon />
                </Icon>
              )}
            </ParticipantActions>
          </ParticipantItem>
        ))}
      </ParticipantDrawer>
    );
  };

  // 레이아웃 렌더링
  const renderLayout = () => {
    // 피어와 로컬 사용자를 결합하여 참가자 목록 생성
    const allParticipants = [
      // 로컬 사용자
      {
        id: 'local',
        name: 'You',
        stream: localStreamRef.current,
        isSpeaking: false,
        isHost: true,
        isMuted: isMuted,
        isVideoOff: isVideoOff,
        isScreenSharing: isScreenSharing,
        audioLevel: 0,
      },
      // 원격 참가자 (피어)
      ...peers.map(peer => {
        const stream = peer.remoteStream || null;

        return {
          id: peer.peerID,
          name: `User ${peer.peerID.substring(0, 4)}`,
          stream: stream,
          isSpeaking: false,
          isHost: false,
          isMuted: false,
          isVideoOff: !stream || !stream.getVideoTracks().length || !stream.getVideoTracks()[0].enabled,
          audioLevel: 0,
        };
      })
    ];
    
    // 선택한 레이아웃 크기에 맞게 더미 참가자 추가
    const dummyParticipants = Array.from({ length: Math.max(0, displayedParticipantCount - allParticipants.length) }, (_, index) => ({
      id: `dummy-${index}`,
      name: `Empty Slot ${index + 1}`,
      stream: null,
      isSpeaking: false,
      isHost: false,
      isMuted: true,
      isVideoOff: true,
      audioLevel: 0,
    }));
    
    // 활성 참가자들 (최대 displayedParticipantCount만큼)
    const activeParticipants = [...allParticipants, ...dummyParticipants].slice(0, displayedParticipantCount);


    // 모바일: 세로 우선 배치
    if (isMobileView) {
      const cols = activeParticipants.length <= 2 ? 1 : 2;
      const rows = Math.ceil(activeParticipants.length / cols);
      return (
        <div
          style={{
            display: "grid",
            height: "100%",
            width: "100%",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
            gap: "0.25rem",
          }}
        >
          {activeParticipants.map((participant) => (
            <div key={participant.id} style={{ width: "100%", height: "100%", minHeight: 0 }}>
              {renderParticipantVideo(participant, activeParticipants.length > 2)}
            </div>
          ))}
        </div>
      );
    }

    // 데스크톱 레이아웃
    switch (activeParticipants.length) {
      case 1:
        return (
          <div style={{ width: "100%", height: "100%" }}>
            {renderParticipantVideo(activeParticipants[0])}
          </div>
        );
      case 2:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr",
              gap: "0.55rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      case 3:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "3fr 1fr",
              gap: "0.55rem",
            }}
          >
            <div style={{ width: "100%", height: "100%" }}>
              {renderParticipantVideo(activeParticipants[0])}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateRows: "1fr 1fr",
                gap: "0.55rem",
                height: "100%",
              }}
            >
              {activeParticipants.slice(1, 3).map((participant) => (
                <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                  {renderParticipantVideo(participant, true)}
                </div>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: "0.55rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      case 5:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "3fr 2fr",
              gap: "0.55rem",
            }}
          >
            <div style={{ width: "100%", height: "100%" }}>
              {renderParticipantVideo(activeParticipants[0])}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr",
                gap: "0.45rem",
                height: "100%",
              }}
            >
              {activeParticipants.slice(1, 5).map((participant) => (
                <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                  {renderParticipantVideo(participant, true)}
                </div>
              ))}
            </div>
          </div>
        );
      case 6:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: "0.55rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      case 7:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "2fr 1fr",
              gap: "0.55rem",
            }}
          >
            <div style={{ width: "100%", height: "100%" }}>
              {renderParticipantVideo(activeParticipants[0])}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr 1fr",
                gap: "0.55rem",
                height: "100%",
              }}
            >
              {activeParticipants.slice(1, 7).map((participant) => (
                <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                  {renderParticipantVideo(participant, true)}
                </div>
              ))}
            </div>
          </div>
        );
      case 8:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: "0.45rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  // 모달이 열려있지 않으면 아무것도 렌더링하지 않음
  if (!isOpen) return null;

  // 회의 시간 포맷팅
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 연결 상태 표시
  const getConnectionStatusDisplay = () => {
    switch(connectionStatus) {
      case 'connected':
        return <span style={{color: 'green'}}>연결됨</span>;
      case 'disconnected':
        return <span style={{color: 'red'}}>연결 끊김</span>;
      case 'error':
        return <span style={{color: 'orange'}}>연결 오류</span>;
      default:
        return null;
    }
  };

  return (
    <ModalContainer>
      <FlexColumn>
        {/* 미디어 선택 모달 */}
        {renderMediaSelector()}
        
        {/* Header */}
        <Header>
          <HeaderLeft>
            <Button variant="ghost" onClick={onClose}>
              <XIcon />
            </Button>
            <div>
            <Title>
                화상 채팅 - <MeetingTimer seconds={meetingTime} /> <ConnectionStatus status={connectionStatus} />
              </Title>
            </div>
          </HeaderLeft>
          <HeaderRight>
            <Button 
              variant="ghost" 
              disabled={displayedParticipantCount <= 2} 
              onClick={decreaseParticipants}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "1rem",
                  width: "1rem",
                }}
              >
                -
              </span>
            </Button>
            <ParticipantCount>
              <UsersIcon />
              <ParticipantCountText>{displayedParticipantCount}인 / 8인 </ParticipantCountText>
            </ParticipantCount>
            <Button 
              variant="ghost" 
              disabled={displayedParticipantCount >= 8}
              onClick={increaseParticipants}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "1rem",
                  width: "1rem",
                }}
              >
                +
              </span>
            </Button>
            <span>[{peers.length + 1}명]</span>
            <Button
              variant="ghost"
              onClick={toggleParticipantDrawer}
              style={{
                backgroundColor: isParticipantDrawerOpen ? "rgba(255, 255, 255, 0.1)" : "transparent",
              }}
            >
              <UsersIcon />
            </Button>
          </HeaderRight>
        </Header>

        {/* Main Content */}
        <MainContent>
          <VideoGrid>
            {renderLayout()}
          </VideoGrid>

          {/* Local Video (Hidden) */}
          <video 
            ref={userVideoRef} 
            autoPlay 
            playsInline 
            muted 
            style={{ display: 'none' }} 
          />

          {/* Toolbox */}
          {isToolboxOpen && (
            <ToolboxContainer>
              <ToolboxButton variant="ghost" onClick={toggleRaiseHand}>
                <HandIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <ChatIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <EmojiIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <SettingsIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <UserPlusIcon />
              </ToolboxButton>
            </ToolboxContainer>
          )}
        </MainContent>

        {/* Participant Drawer */}
        {renderParticipantDrawer()}

        {/* Controls */}
        <Controls>
          <Button variant="destructive" onClick={onClose}>
            <PhoneOffIcon />
          </Button>

          <Button variant={isMuted ? "destructive" : "secondary"} onClick={toggleMute}>
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </Button>

          <Button variant={isVideoOff ? "destructive" : "secondary"} onClick={toggleVideo}>
            {isVideoOff ? <VideoOffIcon /> : <VideoIcon />}
          </Button>

          <Button
            variant={isScreenSharing ? "destructive" : "secondary"}
            onClick={toggleScreenSharing}
          >
            <Share2Icon />
          </Button>
        </Controls>
      </FlexColumn>
    </ModalContainer>
  );
};

export default VideoConferenceModal;